import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { createAlchemyWeb3, Nft } from "@alch/alchemy-web3";
import axios from 'axios';

// ANTD
import { Button, Modal } from 'antd';

// STATE
import { useGlobalState } from '../index';

//MODULES
import Error from '../shared/Error';
import Faq from '../shared/Faq';
import Footer from '../shared/Footer';
import Loader from '../shared/Loader';

// STYLES
import './styles.scss';

// ASSETS
import dgLogo from '../assets/img/menu-logo.svg';

// CONSTANTS
import { ALCHEMY_ERROR_MSG, BUTTON_ERROR_MSG, ERROR_NAVIGATE_URL, METADATA_ERROR_MSG, RETRIEVING_NFT } from './constants';

// ADDRESSES
import { addressList } from './address-list';

export interface Signature {
    address: string;
    message: string;
    signature: string;
};

function Home() {

    const [state, dispatch] = useGlobalState();
    const [claimModalVisible, setClaimModalVisible] = useState<boolean>(false);
    const [loadingState, setLoadingState] = useState<boolean>(false);
    const [errorState, setErrorState] = useState<boolean>(false);
    const [errorMsgState, setErrorMsgState] = useState<string[]>([]);

    const navigate = useNavigate();

    const connectWallet = async (): Promise<void> => {
        if (state.chainId === parseInt(process.env.REACT_APP_NETWORK_ID as string)) {

            const accounts = await window.ethereum?.request({
                method: "eth_requestAccounts",
            });

            if (accounts?.length) {
                dispatch({ user: accounts[0], connected: true });
                await getTokenByAddressAlchemy(accounts[0]);
                setClaimModalVisible(true);
            }

        } else {
            switchNetwork();
        }
    };

    const switchNetwork = async (): Promise<void> => {
        await window.ethereum?.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: `0x${process.env.REACT_APP_NETWORK_ID as string}` }],
        });
    };

    const claimModal = (): void => {
        setClaimModalVisible(true);
    };

    const claim = (): void => {
        navigate('/claim');
    };

    const buyOnOpenSea = (url: string): void => {
        window.open(url);
    };

    const getTokenByAddressAlchemy = async (user: string): Promise<void> => {

        setLoadingState(true);

        const totalNfts: Nft[] = [];

        await getAlchemyNfts(user, totalNfts);
        await checkClaimedTokensAlchemy(totalNfts as Nft[]);

        setLoadingState(false);

    };

    const getAlchemyNfts = async (user: string, totalNfts: Nft[], pageKey?: string): Promise<Nft[] | void> => {
        try {

            const web3 = createAlchemyWeb3(process.env.REACT_APP_ALCHEMY_API as string);
            const nfts = await web3.alchemy.getNfts({ owner: user, contractAddresses: [process.env.REACT_APP_TOKEN_ADDRESS as string], pageKey });

            totalNfts.push(...nfts.ownedNfts);

            if (totalNfts.length < nfts.totalCount) await getAlchemyNfts(user, totalNfts, nfts.pageKey);

        } catch (error: any) {
            setLoadingState(false);
            setErrorState(true);
            setErrorMsgState(ALCHEMY_ERROR_MSG);
        }
    };

    const checkAddressExist = (address: string): boolean => {
        const addressFound = addressList.find((addressItem: string) => address.toLowerCase() === addressItem.toLowerCase());
        if(addressFound) return true;
        return false;
    };

    const checkClaimedTokensAlchemy = async (nfts: Nft[]): Promise<void> => {

        const userTokensMetaData: any[] = [];
        const claimedTokenIds: number[] = [];

        const tShirtTokens: number[] = [];
        const hoodieTokens: number[] = [];
        const sneakerTokens: number[] = [];
        const platinumTokens: number[] = [];

        const tShirtClaimedTokens: number[] = [];
        const hoodieClaimedTokens: number[] = [];
        const sneakerClaimedTokens: number[] = [];
        const platinumClaimedTokens: number[] = [];

        const tShirtNonClaimedTokens: number[] = [];
        const hoodieNonClaimedTokens: number[] = [];
        const sneakerNonClaimedTokens: number[] = [];
        const platinumNonClaimedTokens: number[] = [];

        const tokenIds = nfts.map((nft: Nft) => parseInt(nft.id.tokenId, 16));

        try {

            const metadataResponse = await axios.post(`${process.env.REACT_APP_API_URL}/nft/metadata`, tokenIds);

            for (let i = 0; i < metadataResponse.data.length; i++) {

                const nftMetadata = metadataResponse?.data[i].data;

                userTokensMetaData.push(nftMetadata);

                const tokenClaimedStatus = nftMetadata.attributes[1].value;
                const tokenType = nftMetadata.attributes[0].value;
                const tokenId = nftMetadata.tokenId;

                if (tokenClaimedStatus === 'NO') {

                    if (tokenType === 'T-Shirt') tShirtNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Hoodie') hoodieNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Shoes') sneakerNonClaimedTokens.push(tokenId);
                    if (tokenType === 'Platinum Shoes') platinumNonClaimedTokens.push(tokenId);

                } else if (tokenClaimedStatus === 'YES') {

                    claimedTokenIds.push(tokenId);

                    if (tokenType === 'T-Shirt') tShirtClaimedTokens.push(tokenId);
                    if (tokenType === 'Hoodie') hoodieClaimedTokens.push(tokenId);
                    if (tokenType === 'Shoes') sneakerClaimedTokens.push(tokenId);
                    if (tokenType === 'Platinum Shoes') platinumClaimedTokens.push(tokenId);

                }

                if (tokenType === 'T-Shirt') tShirtTokens.push(tokenId);
                if (tokenType === 'Hoodie') hoodieTokens.push(tokenId);
                if (tokenType === 'Shoes') sneakerTokens.push(tokenId);
                if (tokenType === 'Platinum Shoes') platinumTokens.push(tokenId);
            }

            dispatch({
                claimedTokenIds,
                userTokensMetaData,
                tShirtTokens,
                hoodieTokens,
                sneakerTokens,
                platinumTokens,
                tShirtClaimedTokens,
                hoodieClaimedTokens,
                sneakerClaimedTokens,
                platinumClaimedTokens,
                tShirtNonClaimedTokens,
                hoodieNonClaimedTokens,
                sneakerNonClaimedTokens,
                platinumNonClaimedTokens
            });

        } catch (error: any) {
            setLoadingState(false);
            setErrorState(true);
            setErrorMsgState(METADATA_ERROR_MSG);
        }

    };

    useEffect(() => {
        state.user && getTokenByAddressAlchemy(state.user);
    }, [state.user]);

    if (loadingState) {
        return (
            <div className="claim-nft-container loading">
                <div className="claim-nft-loader-container">
                    <Loader loaderStatus={RETRIEVING_NFT} />
                </div>
            </div>
        );
    };

    if (errorState) {
        return (
            <div className="claim-nft-container error">
                <div className="claim-nft-error-container">
                    <Error errorStatus={errorMsgState} buttonErrorMsg={BUTTON_ERROR_MSG} navigationUrl={ERROR_NAVIGATE_URL} />
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="home-container">
                <div className="claim-status-header">
                    {state.user && checkAddressExist(state.user) ? <a href="/claim-status">Click here to check if an NFT is Claimed</a>:<span>Claim window has ended.</span>}
                </div>
                <div className="home-content main">
                    <div className="home-content-box">
                        <h5>DGFAMILY</h5>
                        <img src={dgLogo} className="dg-logo" />
                        <h1>Realtà Parallela Physical (IRL) Product Claim</h1>
                        <p>Use this site to claim the physical apparel linked to your DGFamily Realtà Parallela NFTs.</p>
                        <p>If you currently hold a Realtà Parallela NFT, please connect your wallet now to begin.</p>
                        {state.user && checkAddressExist(state.user) ? 
                            <>
                                {((state.tShirtNonClaimedTokens.length + state.hoodieNonClaimedTokens.length + state.sneakerNonClaimedTokens.length + state.platinumNonClaimedTokens.length) === 0 && state.connected) ?
                                (<p className="not-own">You do not own any Realtà Parallela Nfts.</p>) : null}
                                <div className="home-actions">
                                    {state.connected ? (
                                        (state.tShirtNonClaimedTokens.length + state.hoodieNonClaimedTokens.length + state.sneakerNonClaimedTokens.length + state.platinumNonClaimedTokens.length) === 0 ? (
                                            <Button type="primary" shape="round" size="large" onClick={() => buyOnOpenSea(process.env.REACT_APP_OPENSEA_DG as string)} loading={loadingState} disabled={loadingState}>Buy on Opensea</Button>
                                        ) : (
                                            <Button type="primary" shape="round" size="large" onClick={() => claimModal()} loading={loadingState} disabled={loadingState || state.chainId !== parseInt(process.env.REACT_APP_NETWORK_ID as string)}>Start Claim</Button>
                                        )
                                    ) : (<Button type="primary" shape="round" size="large" onClick={() => connectWallet()} loading={loadingState} disabled={loadingState}>Connect wallet</Button>)}
                                </div>
                                <div className="claim-link-status-container">
                                    <a href="/claim-status">Click here to check if an NFT is Claimed</a>
                                </div>
                            </>
                            : 
                            <>
                                <p><strong>CLAIM WINDOW HAS ENDED.</strong></p>
                                <div className="home-actions">
                                    <Button type="primary" shape="round" size="large" onClick={() => buyOnOpenSea(process.env.REACT_APP_OPENSEA_DG as string)} loading={loadingState} disabled={loadingState}>Buy on Opensea</Button>
                                </div>
                            </>}
                    </div>
                    <div className="video-container">
                        <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664353719/UNXD/main-hero2_zmzwfz.jpg" preload="none" loop playsInline muted>
                            <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663841231/UNXD/set1_1_twymlt.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>
                {/* <div className="home-footer">
                    <div className="marquee">
                        <div>
                            <span>|| DGFAMILY || DOLCE & GABBANA || UNXD || AIR DROP COLLECTION || NFT || BLACK BOX || GOLD BOX || PLATINUM BOX </span>
                            <span>|| DGFAMILY || DOLCE & GABBANA || UNXD || AIR DROP COLLECTION || NFT || BLACK BOX || GOLD BOX || PLATINUM BOX</span>
                        </div>
                    </div>
                </div> */}
                <div className="home-content">
                    <div className="video-container  add-padding">
                        <video autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664549863/UNXD/sn_h93zai.jpg" preload="none" loop playsInline muted>
                            <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/q_auto/v1663847357/UNXD/Set2_loc4sf.mp4" type="video/mp4" />
                        </video>
                    </div>
                    <div className="home-content-box">
                        <h1>Realtà Parallela NFTs</h1>
                        <p>Originally airdropped to DGFamily Box holders, the Realtà Parallela collection was exclusively designed by Domenico Dolce and Stefano Gabbana for the DGFamily community. The physical collection will be custom produced according to each holder’s choice of style and size. You can purchase Realtà Parallela NFTs on OpenSea and claim your own exclusive Dolce&Gabbana IRL T-shirt, hoodie, or sneakers.</p>
                        <div className="home-actions">
                            <Button type="primary" shape="round" size="large" onClick={() => buyOnOpenSea(process.env.REACT_APP_OPENSEA_DG as string)} loading={loadingState}>Buy on Opensea</Button>
                        </div>
                    </div>
                </div>
                <div className="home-content">
                    <div className="home-content-box">
                        <h1>Join the Family</h1>
                        <p>Realtà Parallela is just a piece of the puzzle: The DGFamily Boxes are your membership to the DGFamily, Dolce&Gabbana’s exclusive NFT community. DGFamily members receive exclusive digital, physical, and experiential privileges as Dolce&Gabbana pushes the boundaries of luxury and culture in the metaverse.</p>
                        <div className="home-actions">
                            <Button type="primary" shape="round" size="large" onClick={() => buyOnOpenSea(process.env.REACT_APP_OPENSEA_BOXES as string)} loading={loadingState}>Buy on Opensea</Button>
                        </div>
                    </div>
                    <div className="video-container right">
                        <video className="boxes" autoPlay={true} poster="https://res.cloudinary.com/dvz8qqgob/image/upload/v1664549861/UNXD/b2_gbxcus.jpg" preload="none" loop playsInline muted>
                            <source src="https://res.cloudinary.com/dvz8qqgob/video/upload/v1663842045/UNXD/boxes_kaxrbj.mp4" type="video/mp4" />
                        </video>
                    </div>
                </div>
                <Faq />
                <Footer />
            </div>
            <Modal centered className="claim-modal-container" open={claimModalVisible} footer={null} onCancel={() => setClaimModalVisible(false)}>
                <h1>Realtà Parallela Physical Claim</h1>
                <p>In the following claim process, you will select the NFTs for which you would like to claim the physical edition. You will then proceed to select your style and size and specify your shipping address. <strong>For your security, please do not share this private URL with anyone else.</strong></p>
                <p>Upon completion, you will receive an email confirmation for your order and your NFTs will be updated to reflect the physical style you selected.</p>
                <Button type="primary" shape="round" size="large" onClick={() => claim()}>Start Claim</Button>
            </Modal>
        </>
    );
}

export default Home;